export interface Chore {
  id: number;
  name: string;
  notes: string;
  intervalDays: number;
  lastDone: string;
}

export interface ScheduledChore extends Chore {
  dueDate: string;
  overdueDays: number;
  isDue: boolean;
  isCritical: boolean;
}

const millisecondsPerDay = 86_400_000;
const calendarDatePattern = /^(\d{4})-(\d{2})-(\d{2})$/;

export function calendarDay(date: string): number {
  const match = calendarDatePattern.exec(date);
  if (!match) throw new Error("Date must use YYYY-MM-DD format");
  const year = Number(match[1]); const month = Number(match[2]); const day = Number(match[3]);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  if (parsed.getUTCFullYear() !== year || parsed.getUTCMonth() !== month - 1 || parsed.getUTCDate() !== day) throw new Error("Date is not a valid calendar day");
  return Math.floor(parsed.getTime() / millisecondsPerDay);
}

export function calendarDate(day: number): string { return new Date(day * millisecondsPerDay).toISOString().slice(0, 10); }
export function daysUntilCalendarDate(date: string, today: string): number { return calendarDay(date) - calendarDay(today); }
export function localCalendarDate(date = new Date()): string { return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`; }
export function localCalendarDateDaysAgo(days: number, date = new Date()): string { return calendarDate(calendarDay(localCalendarDate(date)) - days); }
export function isCalendarDate(value: unknown): value is string { if (typeof value !== "string") return false; try { calendarDay(value); return true; } catch { return false; } }

export function scheduleChore(chore: Chore, today: string): ScheduledChore {
  const lastDoneDay = calendarDay(chore.lastDone); const todayDay = calendarDay(today); const dueDay = lastDoneDay + chore.intervalDays;
  return { ...chore, dueDate: calendarDate(dueDay), overdueDays: Math.max(0, todayDay - dueDay), isDue: todayDay >= dueDay, isCritical: todayDay > lastDoneDay + chore.intervalDays * 2 };
}

export function dueChoresByImportance(chores: Chore[], today: string): ScheduledChore[] {
  return chores.map(chore => scheduleChore(chore, today)).filter(chore => chore.isDue).sort((left, right) => right.overdueDays - left.overdueDays || left.name.localeCompare(right.name) || left.id - right.id);
}

export function allChoresBySchedule(chores: Chore[], today: string): ScheduledChore[] {
  return chores.map(chore => scheduleChore(chore, today)).sort((left, right) => left.isDue !== right.isDue ? (left.isDue ? -1 : 1) : left.isDue ? right.overdueDays - left.overdueDays || left.name.localeCompare(right.name) || left.id - right.id : calendarDay(left.dueDate) - calendarDay(right.dueDate) || left.name.localeCompare(right.name) || left.id - right.id);
}
